// Libs
import classNames from 'classnames/bind';
import { uid } from 'uid';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
// Components, Layouts, Pages
import { ItemPartOrder, LabelValueSchedule } from '~/components/module/order';
import { BaseLabel, BaseScheduleKanban, Loading, UnderDevelopment } from '~/components/common';
import CustomHeaderColumn from './customHeaderColumn/CustomHeaderColumn';
// Others
import { IScheduleList } from '~/utils/interface/schedule';
import { ColumnTypeKanban } from '~/utils/interface/common';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { convertTimeToHHmm, customToast, formatDate, getFullName } from '~/utils/helper/common';
import { DateFormatEnum, OrderPickupEnum, ScheduleSplitType, ToastTypeEnum } from '~/utils/enum/common';
import {
  IOrderSplit,
  IJobItemSlip,
  IOrderEventList,
  IFormSliptOrder,
  IBodySplitOrder,
  IFormSplitOrder,
  IParamQueryOrderSplit,
} from '~/utils/interface/order';
import { orderActions } from '~/thunks/order/orderSlice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { getOrderSplit, splitOrder } from '~/thunks/order/orderThunk';
import { convertColumnsToOrders, convertOrderToKanban } from './helper';
// Styles, images, icons
import styles from './SplitFormModal.module.scss';
import { icons } from '~/assets';

type Props = {
  item: IOrderEventList | IScheduleList;
  type: ScheduleSplitType;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const SplitFormModal = (props: Props) => {
  //#region Destructuring Props
  const { item, type, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const dispatch = useAppDispatch();

  const {
    control,
    watch,
    setValue,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormSliptOrder>();

  const jobsWatch = useWatch({
    control,
    name: 'jobs',
    defaultValue: [],
  });

  const { fields: jobs, append: appendJob } = useFieldArray({
    control,
    name: 'jobs',
  });

  const { append: appendJobItem, remove: removeJobItem } = useFieldArray({
    control,
    name: 'jobItems',
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderSplit, setOrderSplit] = useState<IOrderSplit>();
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetOrderSplit();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetOrderSplit = () => {
    setIsLoading(true);

    const params: IParamQueryOrderSplit = {
      orderId: item?.orderId,
      isDelivery: item?.isDelivery || false,
      isPickup: item?.isPickup || false,
    };

    dispatch(getOrderSplit(params))
      .unwrap()
      .then((res) => {
        const { data } = res;
        setOrderSplit(data);

        const dataConvertSlip = convertOrderToKanban(data);

        reset({ jobItems: dataConvertSlip.jobItems, jobs: dataConvertSlip.jobs });
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSave = (data: IFormSliptOrder) => {
    const hasInvalidJob = data?.jobItems
      ?.filter((job) => job.id !== 'unassigned')
      ?.some((job) => !job?.items || job?.items?.length === 0);

    if (hasInvalidJob) {
      customToast(ToastTypeEnum.ERROR, t('order_event_job_must_have_at_least_one_item'));
      return;
    }
    const body = convertColumnsToOrders(data);

    const payload: IBodySplitOrder = {
      jobs: {
        jobs: body as IFormSplitOrder[],
        deleteJobIds: data.deleteJobIds,
        isDelivery: item?.isDelivery || false,
        isPickup: item?.isPickup || false,
        ...(type === ScheduleSplitType.VEHICLE_SCHEDULE && {
          startDate: item?.startDate || undefined,
          startTime: convertTimeToHHmm(item?.startTime) || undefined,
        }),
      },
      orderId: item.orderId,
    };

    setIsLoading(true);

    dispatch(splitOrder(payload))
      .unwrap()
      .then(() => {
        handleCloseModal();
        dispatch(orderActions.setRefreshOrderEventList(true));
        dispatch(scheduleActions.setRefreshScheduleList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddNewPart = () => {
    let maxPartNumber = 1;
    const partNameRegex = /^Part (\d+)$/i;

    jobs.forEach((job) => {
      if (job.title) {
        const match = job.title.match(partNameRegex);
        if (match) {
          const number = parseInt(match[1], 10);
          if (number > maxPartNumber) {
            maxPartNumber = number;
          }
        }
      }
    });

    const nameColumn = `Part ${maxPartNumber + 1}`;

    const uuid = uid();
    const newColumn: ColumnTypeKanban<IJobItemSlip> = {
      uuid: uuid,
      id: EMPTY_STRING,
      name: nameColumn,
      items: [],
    };

    const newJob: IFormSplitOrder = {
      title: nameColumn,
      type: EMPTY_STRING,
      foremanId: EMPTY_STRING,
      driverId: EMPTY_STRING,
      truckId: EMPTY_STRING,
      trailerId: EMPTY_STRING,
      crewIds: [],
      loaderId: [],
      orderItemIds: [],
    };

    appendJob(newJob);
    appendJobItem(newColumn);
  };

  const handleDeleteColumn = (index: number) => {
    const newJob = jobsWatch.filter((_, i) => i !== index);
    setValue('jobs', newJob);

    removeJobItem(index);
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleCloseModal = () => {
    onClose && onClose();
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='splitFormModalComponent'
      aria-labelledby='splitFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='splitFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel label={t('split_form_title', { orderId: orderSplit?.id })} styleType='bigTitle' />

            {orderSplit?.deliveryOrPickUp !== OrderPickupEnum.CUSTOMER_PICKUPS && (
              <Button
                className={cx('buttonNewPart')}
                startIcon={
                  <Avatar
                    className={cx('iconAdd')}
                    src={icons.commonIconAddPrimary}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                  />
                }
                onClick={handleAddNewPart}
              >
                {t('split_form_button_new_part')}
              </Button>
            )}
          </Stack>
          <Stack direction={'row'} spacing={'5px'}>
            <LabelValueSchedule
              label={t('split_form_label_assigned')}
              value={
                getFullName({
                  firstName: orderSplit?.assignedUser?.firstName,
                  lastName: orderSplit?.assignedUser?.lastName,
                }) || EMPTY_VALUE
              }
              typeStyle='big'
            />
            <LabelValueSchedule
              label={t('split_form_label_time')}
              value={formatDate(orderSplit?.startDate, DateFormatEnum.HH_mm_A) || EMPTY_VALUE}
              typeStyle='big'
            />
            <LabelValueSchedule
              label={t('split_form_label_location')}
              value={item?.address?.address || EMPTY_VALUE}
              typeStyle='big'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <FormProvider
        {...({
          control,
          watch,
          reset,
          setValue,
          getValues,
          handleSubmit,
          formState: { errors },
        } as UseFormReturn<IFormSliptOrder>)}
      >
        <DialogContent className={cx('content')} dividers>
          <Controller
            control={control}
            name='jobItems'
            render={({ field: { onChange, value } }) => {
              return (
                <BaseScheduleKanban
                  gapColumn={12}
                  minHeightColumn={450}
                  data={value || []}
                  onChange={(data) => {
                    onChange(data.allColumns);
                  }}
                  HeaderColumn={(props) => (
                    <CustomHeaderColumn orderSplit={orderSplit} {...props} onDelete={handleDeleteColumn} />
                  )}
                  renderItem={(data) => <ItemPartOrder item={data} />}
                />
              );
            }}
          />
        </DialogContent>

        <DialogActions className={cx('groupButton')}>
          <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' className={cx('buttonSave')} onClick={handleSubmit(handleSave)}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </DialogActions>
      </FormProvider>

      {isLoading && <Loading />}
      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Dialog>
  );
};

export default SplitFormModal;
